import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import {
  Account,
  CreateAccountRequest,
  UpdateAccountRequest,
  User,
  CreateUserRequest,
  BulkCreateUsersRequest,
  UserSearchParams,
  PasswordResetRequest,
  UserStatusUpdateRequest,
  ApiResponse,
  BulkCreateResponse,
  BatchStatus,
  BatchHistoryResponse,
  CsvDownload,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 30000,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      async (config) => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn('Failed to get auth token:', error);
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Redirect to login if unauthorized
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Account Management
  async getAccounts(environment?: string, status?: string): Promise<Account[]> {
    const params: any = {};
    if (environment) params.environment = environment;
    if (status) params.status = status;

    const response: AxiosResponse<ApiResponse<Account[]>> = await this.api.get('/accounts', { params });
    return response.data.data || [];
  }

  async getAccount(accountId: string): Promise<Account> {
    const response: AxiosResponse<ApiResponse<Account>> = await this.api.get(`/accounts/${accountId}`);
    if (!response.data.data) {
      throw new Error('Account not found');
    }
    return response.data.data;
  }

  async createAccount(account: CreateAccountRequest): Promise<Account> {
    const response: AxiosResponse<ApiResponse<Account>> = await this.api.post('/accounts', account);
    if (!response.data.data) {
      throw new Error('Failed to create account');
    }
    return response.data.data;
  }

  async updateAccount(accountId: string, updates: UpdateAccountRequest): Promise<Account> {
    const response: AxiosResponse<ApiResponse<Account>> = await this.api.put(`/accounts/${accountId}`, updates);
    if (!response.data.data) {
      throw new Error('Failed to update account');
    }
    return response.data.data;
  }

  async deleteAccount(accountId: string): Promise<void> {
    await this.api.delete(`/accounts/${accountId}`);
  }

  async testAccountConnectivity(accountId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/accounts/${accountId}/test`);
    return response.data.data;
  }

  // User Management
  async createUser(user: CreateUserRequest): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/users', user);
      if (!response.data.data) {
        throw new Error('Failed to create user');
      }
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to create user');
    }
  }

  async bulkCreateUsers(request: BulkCreateUsersRequest): Promise<BulkCreateResponse> {
    const response: AxiosResponse<ApiResponse<BulkCreateResponse>> = await this.api.post('/users/bulk', request);
    if (!response.data.data) {
      throw new Error('Failed to create users in bulk');
    }
    return response.data.data;
  }

  async searchUsers(params: UserSearchParams): Promise<{ users: User[]; count: number; total_found?: number; next_token?: string }> {
    const response: AxiosResponse<ApiResponse<{ users: User[]; count: number; total_found?: number; next_token?: string }>> = 
      await this.api.get('/users/search', { params });
    if (!response.data.data) {
      throw new Error('Failed to search users');
    }
    return response.data.data;
  }

  async exportUsersCsv(params: Omit<UserSearchParams, 'limit' | 'next_token'>): Promise<CsvDownload> {
    const response = await this.api.get('/users/export', {
      params,
      responseType: 'blob',
    });

    const disposition =
      response.headers?.['content-disposition'] ||
      response.headers?.['Content-Disposition'] ||
      '';
    let filename = `users-${params.account_id}.csv`;
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)/i);
    if (filenameMatch && filenameMatch[1]) {
      try {
        filename = decodeURIComponent(filenameMatch[1]);
      } catch {
        filename = filenameMatch[1];
      }
    }

    return {
      blob: response.data,
      filename,
    };
  }

  async resetUserPassword(accountId: string, username: string, request: PasswordResetRequest): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
        `/users/${accountId}/${username}/reset-password`,
        request
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to reset user password');
    }
  }

  async updateUserStatus(accountId: string, username: string, request: UserStatusUpdateRequest): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(
        `/users/${accountId}/${username}/status`,
        request
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to update user status');
    }
  }

  async deleteUser(accountId: string, username: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(
        `/users/${accountId}/${username}`
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to delete user');
    }
  }

  // Admin User Management
  async getAdminUsers(): Promise<any[]> {
    try {
      const response = await this.api.get('/admin/users');
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.error_type === 'MFA_REQUIRED') {
        const mfaError = new Error(error.response.data.message);
        (mfaError as any).type = 'MFA_REQUIRED';
        throw mfaError;
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to load admin users');
    }
  }

  async createAdminUser(adminData: any): Promise<any> {
    try {
      const response = await this.api.post('/admin/users', adminData);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.error_type === 'MFA_REQUIRED') {
        const mfaError = new Error(error.response.data.message);
        (mfaError as any).type = 'MFA_REQUIRED';
        throw mfaError;
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to create admin user');
    }
  }

  async removeAdminUser(username: string): Promise<any> {
    try {
      const response = await this.api.delete(`/admin/users/${username}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.error_type === 'MFA_REQUIRED') {
        const mfaError = new Error(error.response.data.message);
        (mfaError as any).type = 'MFA_REQUIRED';
        throw mfaError;
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to remove admin user');
    }
  }

  async resetUserMFA(username: string): Promise<any> {
    try {
      const response = await this.api.post(`/admin/users/${username}/reset-mfa`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.error_type === 'MFA_REQUIRED') {
        const mfaError = new Error(error.response.data.message);
        (mfaError as any).type = 'MFA_REQUIRED';
        throw mfaError;
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to reset user MFA');
    }
  }

  // Utility methods
  async getCurrentUserInfo(): Promise<any> {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error;
    }
  }

  // CSV Processing
  parseCsv(csvContent: string): any[] {
    const lines = csvContent.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim());
    const requiredHeaders = ['username', 'email', 'name'];
    
    for (const required of requiredHeaders) {
      if (!headers.includes(required)) {
        throw new Error(`Missing required column: ${required}`);
      }
    }

    const users = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        throw new Error(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}`);
      }

      const user: any = {};
      headers.forEach((header, index) => {
        user[header] = values[index];
      });

      // Validate required fields
      for (const required of requiredHeaders) {
        if (!user[required]) {
          throw new Error(`Missing required field '${required}' in row ${i + 1}`);
        }
      }

      users.push(user);
    }

    return users;
  }

  // Batch Status Methods
  async getBatchStatus(batchId: string): Promise<BatchStatus> {
    try {
      const response: AxiosResponse<ApiResponse<BatchStatus>> = await this.api.get(
        `/batches/${batchId}`
      );
      return response.data.data!;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to get batch status');
    }
  }

  async getBatchHistory(): Promise<BatchHistoryResponse> {
    try {
      const response: AxiosResponse<ApiResponse<BatchHistoryResponse>> = await this.api.get(
        '/batches/history'
      );
      return response.data.data!;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'Failed to get batch history');
    }
  }

  generateCsvTemplate(): string {
    const headers = ['username', 'email', 'name', 'department', 'role'];
    const sampleData = [
      'john.doe', 'john.doe@example.com', 'John Doe', 'Engineering', 'Developer',
      'jane.smith', 'jane.smith@example.com', 'Jane Smith', 'Marketing', 'Manager'
    ];

    const csv = [
      headers.join(','),
      sampleData.slice(0, 5).join(','),
      sampleData.slice(5, 10).join(',')
    ].join('\n');

    return csv;
  }
}

const apiService = new ApiService();
export default apiService;